import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { ReportSummary } from '@/types/coach';
import { Button } from '@/components/ui/button';
import { ReportCard } from './ReportCard';
import { ReportGenerateModal } from './ReportGenerateModal';

interface Props {
  clientId: string;
}

export function ReportsList({ clientId }: Props) {
  const [reports, setReports] = useState<ReportSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.coach.reports
      .list(clientId)
      .then((res) => {
        if (!cancelled) setReports(res.reports);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, reloadCounter]);

  function reload() {
    setReloadCounter((c) => c + 1);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold">Berichte</h2>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          Neuen Bericht erstellen
        </Button>
      </div>

      {loading && (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="text-destructive">{error}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={reload}>
            Erneut versuchen
          </Button>
        </div>
      )}

      {!loading && !error && reports && reports.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-muted/40 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Noch kein Bericht erstellt.
          </p>
        </div>
      )}

      {!loading && !error && reports && reports.length > 0 && (
        <div className="space-y-2">
          {reports.map((r) => (
            <ReportCard key={r.id} clientId={clientId} report={r} />
          ))}
        </div>
      )}

      {modalOpen && (
        <ReportGenerateModal
          clientId={clientId}
          onClose={() => {
            setModalOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}
